import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { faCircleExclamation, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

import { issueText } from "./issueText";
import { ScoreIssue } from "./scoreIssues";

interface IssueBarProps {
	issues: ScoreIssue[];
	/** "segments" or "personas" — what the title says cannot be scored yet. */
	subject: string;
	onSelect: (issue: ScoreIssue) => void;
}

/**
 * The red bar at the top of a settings page (PREC-4055): every open issue as a
 * clickable label that selects and flashes the row it names.
 */
export const IssueBar: FunctionComponent<IssueBarProps> = ({ issues, subject, onSelect }) => {
	const { t } = useTranslation();

	if (issues.length === 0) {
		return null;
	}

	return (
		<div
			className="mb-3.5 rounded-lg border border-status-error-light bg-error-light px-3 py-2.5"
			data-testid="issue-bar"
		>
			<div className="mb-2 flex items-center gap-2 text-xs font-semibold text-status-error">
				<Icon className="h-3.5 w-3.5" icon={faCircleExclamation} />
				{t("leadScoring.detail.issuesBefore", { count: issues.length, subject })}
			</div>
			<div className="flex flex-wrap gap-1.5">
				{issues.map((issue, index) => (
					<button
						key={`${issue.kind}-${issue.id}-${issue.personaId ?? ""}-${index}`}
						className="flex h-[26px] max-w-full cursor-pointer items-center gap-1.5 rounded-md border border-status-error-light bg-white px-2.5 text-[11px] font-medium text-status-error hover:border-status-error"
						type="button"
						onClick={() => onSelect(issue)}
					>
						<Icon className="h-3 w-3 shrink-0" icon={faTriangleExclamation} />
						<span className="truncate">{issueText(t, issue)}</span>
					</button>
				))}
			</div>
		</div>
	);
};
